import React, { useState } from "react";
import { FormikValues } from "formik";
import { VideoFile, PlayArrow, Delete, Visibility } from "@mui/icons-material";
import { useUploadVideoMutation } from "../../api/fileUpload";

interface VideoFieldProps {
  name: string;
  label: string;
  formik: FormikValues;
  width?: "full" | "half";
}

export const VideoField: React.FC<VideoFieldProps> = ({
  name,
  label,
  formik,
  width = "full",
}) => {
  const [videoUrl, setVideoUrl] = useState(formik.values.videoLink || "");
  const [thumbs, setThumbs] = useState(formik.values.thumbs || [""]);
  const [isPlaying, setIsPlaying] = useState(false);

  const [upload, { isLoading: uploadIsLoading, isSuccess: uploadSuccess }] =
    useUploadVideoMutation({});

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event?.target.files && event?.target?.files[0];
    if (file) {
      // Check if file is a video
      if (!file.type.startsWith("video/")) {
        alert("Please select a video file");
        return;
      }

      const formData = new FormData();
      formData.append("video", file);
      formData.append("fileName", formik.values.fileName || file.name);

      upload(formData)
        .unwrap()
        .then((res) => {
          res.data.screenshots &&
            formik.setFieldValue("thumbImage", res.data.screenshots[0]);
          res.data.screenshots &&
            formik.setFieldValue("thumbs", res.data.screenshots);
          res.data.videoUrl &&
            formik.setFieldValue("videoLink", res.data.videoUrl);
          res.data.screenshots && setThumbs(res.data.screenshots);
          res.data.videoUrl && setVideoUrl(res.data.videoUrl);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const handleRemoveVideo = () => {
    setVideoUrl("");
    setThumbs([""]);
    formik.setFieldValue("videoLink", "");
    formik.setFieldValue("thumbs", [""]);
    formik.setFieldValue("thumbImage", "");
  };

  const handlePreviewVideo = () => {
    if (videoUrl) {
      window.open(videoUrl, "_blank");
    }
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <div className={`${width === "half" ? "w-[48%]" : "w-full"} mb-4`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
        {!videoUrl ? (
          <div className="text-center">
            <VideoFile className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-2">
              <label
                htmlFor={`video-${name}`}
                className="cursor-pointer bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
              >
                Upload Video
              </label>
              <input
                id={`video-${name}`}
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Only video files are allowed
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Video Preview */}
            <div className="relative">
              <div className="w-full max-w-[500px] aspect-video bg-gray-100 rounded-lg overflow-hidden mx-auto">
                <video
                  src={videoUrl}
                  className="w-full h-full object-cover"
                  controls
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                />
              </div>
              
              {/* Action Buttons */}
              <div className="absolute top-2 right-2 flex space-x-2">
                <button
                  type="button"
                  onClick={handlePreviewVideo}
                  className="p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  title="Preview Video"
                >
                  <Visibility className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleRemoveVideo}
                  className="p-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  title="Remove Video"
                >
                  <Delete className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Thumbnails */}
            {thumbs.length > 0 && thumbs[0] !== "" && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-700">Generated Thumbnails:</h3>
                <div className="flex space-x-2 overflow-x-auto pb-2">
                  {thumbs.map((thumb: string, index: number) => (
                    <div
                      key={index}
                      className="flex-shrink-0 max-w-[33%] h-auto border-2 border-gray-200 rounded-lg overflow-hidden"
                    >
                      <img
                        src={thumb}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Status */}
            {uploadIsLoading && (
              <div className="text-center text-sm text-blue-600">
                Uploading video...
              </div>
            )}
          </div>
        )}
      </div>
      
      {formik.touched[name] && formik.errors[name] && (
        <p className="text-red-500 text-xs mt-1">{formik.errors[name]}</p>
      )}
    </div>
  );
};
